import { AfterViewInit, Directive, ElementRef, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudItemComponent, UserStorage, isObjectValidator, onValueChangeEvent, requiredValidator } from '@scisys-uim/uim-common';
import { AssetsUser, BUSINESS_UNIT_EMAIL_CONTACT_METADATA, BUSINESS_UNIT_EMAIL_CONTACT_TYPE, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, BusinessUnitEmailContact, COST_CENTRE_METADATA, COST_CENTRE_TYPE, CostCentre, RANGER_SOURCE_MAP_METADATA, RANGER_SOURCE_MAP_TYPE, RANGER_TYPE, Ranger, RangerSourceMap, RefCode, setDefaultLocationFromUser } from 'assets-model';
import { BusinessUnitEmailContactService, BusinessUnitService, CostCentreService, RangerService, RangerSourceMapService, RefCodeStorage } from 'assets-service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';


@Directive()
export abstract class RangerItemComponentBase extends CrudItemComponent<Ranger, number> implements OnInit, AfterViewInit {

  protected systemConfig: any;
  
  public form: FormGroup;
  
  businessUnitSelectionList: BusinessUnit[];

  costCentreSelectionList: CostCentre[];

  policeContactSelectionList: BusinessUnitEmailContact[];

  rangerSourceDefaultDebounceMillis: number = 500;
  @ViewChild('rangerSource') rangerSourceInput: ElementRef;
  private rangerSourceSelectionListSubject: Subject<RangerSourceMap[]> = new BehaviorSubject([]);
  rangerSourceSelectionList: Observable<RangerSourceMap[]> = this.rangerSourceSelectionListSubject.asObservable();

  statusSelectionList: RefCode[];


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected rangerService: RangerService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected policeContactService: BusinessUnitEmailContactService,
    protected rangerSourceService: RangerSourceMapService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(RANGER_TYPE, fb, elementRef, router, route, userStorage, dialog, rangerService, injector, snackbar);
  }

  createNewItem() {
    return new Ranger();
  }
  
  createForm(): FormGroup {
    return this.fb.group({
      rangerId: [null, []],
      businessUnit: [null, [requiredValidator,]],
      costCentre: [null, []],
      policeContact: [null, []],
      rangerSource: [null, [isObjectValidator,]],
      addr1: [null, [requiredValidator,]],
      addr2: [null, []],
      addr3: [null, []],
      addressChangeNotifiedOn: [null, []],
      addressChangedDate: [null, []],
      county: [null, []],
      createdBy: [null, []],
      createdDate: [null, []],
      currentFirearmCount: [null, []],
      currentPersonalFirearmCount: [null, []],
      email: [null, [requiredValidator,]],
      firearmsAuthorityExpiryDate: [null, []],
      firearmsAuthorityIssueDate: [null, []],
      lockVersion: [null, []],
      mobilePhone: [null, []],
      name: [null, [requiredValidator,]],
      national: [null, []],
      postcode: [null, [requiredValidator,]],
      provisionalRanger: [null, []],
      status: [null, [requiredValidator,]],
      town: [null, []],
      updatedBy: [null, []],
      updatedDate: [null, []],
      volunteerRanger: [null, []],
      wildlifeManagerRanger: [null, []]
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.wireRangerSourceAutocomplete();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }
  
  protected initSystemConfig(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initSystemConfig(config);
  }
  
  setDefaults() {
    if (!this.readOnly && this.createMode) {
      setDefaultLocationFromUser(this.userDetails, this.item);

      // re-trigger updating drop-downs.
      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (!this.createMode) {
      this.rangerId?.disable();
    }
    this.currentFirearmCount?.disable();
    this.currentPersonalFirearmCount?.disable();
    this.firearmsAuthorityExpiryDate?.disable();
    this.national?.disable();
  }
  
  wireRangerSourceAutocomplete(): void {
    if (this.rangerSource) {
      onValueChangeEvent(this.rangerSource, value => {
        if (value == "") {
          value = null;
          if (this.item) {
            this.item.rangerSource = null;
          }
        }
        if (value == null || typeof value == 'string') {
          this.rangerSourceSelectionListFilter(value);
        }
      }, {
        debounceMillis: this.getRangerSourceLookupDebounceTime()
      });
    }
  }
  
  rangerIdChanging(value: number, unsetDependents: boolean = true) {}
  
  rangerIdChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.rangerIdChangedByUser(value, unsetDependents);
    }
  }  
  
  rangerIdChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }
  
  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = BUSINESS_UNIT_TYPE.entityDecorator.ensureContained(this.item && this.item.businessUnit, data.items);
      }
    );
  }

  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {}
  
  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.businessUnitChangedByUser(value, unsetDependents);
    }
  }  
  
  businessUnitChangedByUser(value: BusinessUnit, unsetDependents: boolean = true) {
  }
  
  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }
  
  costCentreDisplay(item: CostCentre) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return COST_CENTRE_TYPE.description(item);
  }
  
  costCentreLoadSelectionList() {
    this.costCentreService.findAll().subscribe(
      data => {
        this.costCentreSelectionList = COST_CENTRE_TYPE.entityDecorator.ensureContained(this.item && this.item.costCentre, data.items);
      }
    );
  }

  costCentreChanging(value: CostCentre, unsetDependents: boolean = true) {}
  
  costCentreChanged(value: CostCentre, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.costCentreChangedByUser(value, unsetDependents);
    }
  }  
  
  costCentreChangedByUser(value: CostCentre, unsetDependents: boolean = true) {
  }
  
  costCentreCompare(a: any, b: any) {
    return COST_CENTRE_METADATA.equals(a, b);
  }
  
  policeContactDisplay(item: BusinessUnitEmailContact) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_EMAIL_CONTACT_TYPE.description(item);
  }
  
  policeContactLoadSelectionList() {
    this.policeContactService.findAll().subscribe(
      data => {
        this.policeContactSelectionList = BUSINESS_UNIT_EMAIL_CONTACT_TYPE.entityDecorator.ensureContained(this.item && this.item.policeContact, data.items);
      }
    );
  }

  policeContactChanging(value: BusinessUnitEmailContact, unsetDependents: boolean = true) {}
  
  policeContactChanged(value: BusinessUnitEmailContact, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.policeContactChangedByUser(value, unsetDependents);
    }
  }  
  
  policeContactChangedByUser(value: BusinessUnitEmailContact, unsetDependents: boolean = true) {
  }
  
  policeContactCompare(a: any, b: any) {
    return BUSINESS_UNIT_EMAIL_CONTACT_METADATA.equals(a, b);
  }
  
  rangerSourceDisplay(item: RangerSourceMap) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return RANGER_SOURCE_MAP_TYPE.description(item);
  }
  
  getRangerSourceLookupDebounceTime(): number {
    if (this.systemConfig?.rangerSourceMap?.lookup?.debounceTime != null) {
      return this.systemConfig.rangerSourceMap.lookup.debounceTime;
    }
    return this.rangerSourceDefaultDebounceMillis;
  }
  
  getRangerSourceLookupMinCharacters(): number {
    if (this.systemConfig?.rangerSourceMap?.lookup?.minCharacters != null) {
      return this.systemConfig.rangerSourceMap.lookup.minCharacters;
    }
    return 2;
  }

  rangerSourceSelectionListFilter(text: string) {
    if (text == null || text.length < this.getRangerSourceLookupMinCharacters()) {
      this.rangerSourceSelectionListSubject.next([]);
      return;
    }
    this.rangerSourceSelectionListFilterLoad(text).subscribe(items => {
      this.rangerSourceSelectionListSubject.next(items);
    });
  }

  abstract rangerSourceSelectionListFilterLoad(text: string): Observable<RangerSourceMap[]>;

  rangerSourceChanging(value: RangerSourceMap, unsetDependents: boolean = true) {}
  
  rangerSourceChanged(value: RangerSourceMap, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.rangerSourceChangedByUser(value, unsetDependents);
    }
  }  
  
  rangerSourceChangedByUser(value: RangerSourceMap, unsetDependents: boolean = true) {
  }
  
  rangerSourceCompare(a: any, b: any) {
    return RANGER_SOURCE_MAP_METADATA.equals(a, b);
  }
  
  addr1Changing(value: string, unsetDependents: boolean = true) {}
  
  addr1Changed(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.addr1ChangedByUser(value, unsetDependents);
    }
  }  
  
  addr1ChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  addr2Changing(value: string, unsetDependents: boolean = true) {}
  
  addr2Changed(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.addr2ChangedByUser(value, unsetDependents);
    }
  }  
  
  addr2ChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  addr3Changing(value: string, unsetDependents: boolean = true) {}
  
  addr3Changed(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.addr3ChangedByUser(value, unsetDependents);
    }
  }  
  
  addr3ChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  addressChangeNotifiedOnChanging(value: Date, unsetDependents: boolean = true) {}
  
  addressChangeNotifiedOnChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.addressChangeNotifiedOnChangedByUser(value, unsetDependents);
    }
  }  
  
  addressChangeNotifiedOnChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  addressChangedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  addressChangedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.addressChangedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  addressChangedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  countyChanging(value: string, unsetDependents: boolean = true) {}
  
  countyChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.countyChangedByUser(value, unsetDependents);
    }
  }  
  
  countyChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByChangedByUser(value, unsetDependents);
    }
  }  
  
  createdByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdDateChangedByUser(value, unsetDependents);
    }
  }  
  
  createdDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  
  
  emailChanging(value: string, unsetDependents: boolean = true) {}
  
  emailChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.emailChangedByUser(value, unsetDependents);
    }
  }  
  
  emailChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  
  firearmsAuthorityIssueDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  firearmsAuthorityIssueDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.firearmsAuthorityIssueDateChangedByUser(value, unsetDependents);
    }
  }  
  
  firearmsAuthorityIssueDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  lockVersionChanging(value: number, unsetDependents: boolean = true) {}
  
  lockVersionChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionChangedByUser(value, unsetDependents);
    }
  }  
  
  lockVersionChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  mobilePhoneChanging(value: string, unsetDependents: boolean = true) {}
  
  mobilePhoneChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.mobilePhoneChangedByUser(value, unsetDependents);
    }
  }  
  
  mobilePhoneChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  nameChanging(value: string, unsetDependents: boolean = true) {}
  
  nameChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.nameChangedByUser(value, unsetDependents);
    }
  }  
  
  nameChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  
  postcodeChanging(value: string, unsetDependents: boolean = true) {}
  
  postcodeChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.postcodeChangedByUser(value, unsetDependents);
    }
  }  
  
  postcodeChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  provisionalRangerChanging(value: boolean, unsetDependents: boolean = true) {}
  
  provisionalRangerChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.provisionalRangerChangedByUser(value, unsetDependents);
    }
  }  
  
  provisionalRangerChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  statusLoadSelectionList() {
    this.refCodeStorage.getDomainElements('RANGER_STATUS').subscribe(
      data => {
        this.statusSelectionList = data;
      }
    );
  }

  statusChanging(value: string, unsetDependents: boolean = true) {}
  
  statusChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.statusChangedByUser(value, unsetDependents);
    }
  }  
  
  statusChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  townChanging(value: string, unsetDependents: boolean = true) {}
  
  townChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.townChangedByUser(value, unsetDependents);
    }
  }  
  
  townChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  updatedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedByChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  volunteerRangerChanging(value: boolean, unsetDependents: boolean = true) {}
  
  volunteerRangerChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.volunteerRangerChangedByUser(value, unsetDependents);
    }
  }  
  
  volunteerRangerChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  wildlifeManagerRangerChanging(value: boolean, unsetDependents: boolean = true) {}
  
  wildlifeManagerRangerChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.wildlifeManagerRangerChangedByUser(value, unsetDependents);
    }
  }  
  
  wildlifeManagerRangerChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get rangerId() {
    return this.control('rangerId');
  }
  
  get businessUnit() {
    return this.control('businessUnit');
  }
  
  get costCentre() {
    return this.control('costCentre');
  }
  
  get policeContact() {
    return this.control('policeContact');
  }
  
  get rangerSource() {
    return this.control('rangerSource');
  }
  
  get addr1() {
    return this.control('addr1');
  }
  
  get addr2() {
    return this.control('addr2');
  }
  
  get addr3() {
    return this.control('addr3');
  }
  
  get addressChangeNotifiedOn() {
    return this.control('addressChangeNotifiedOn');
  }
  
  get addressChangedDate() {
    return this.control('addressChangedDate');
  }
  
  get county() {
    return this.control('county');
  }
  
  get createdBy() {
    return this.control('createdBy');
  }
  
  get createdDate() {
    return this.control('createdDate');
  }
  
  get currentFirearmCount() {
    return this.control('currentFirearmCount');
  }
  
  get currentPersonalFirearmCount() {
    return this.control('currentPersonalFirearmCount');
  }
  
  get email() {
    return this.control('email');
  }
  
  get firearmsAuthorityExpiryDate() {
    return this.control('firearmsAuthorityExpiryDate');
  }
  
  get firearmsAuthorityIssueDate() {
    return this.control('firearmsAuthorityIssueDate');
  }
  
  get lockVersion() {
    return this.control('lockVersion');
  }
  
  get mobilePhone() {
    return this.control('mobilePhone');
  }
  
  get name() {
    return this.control('name');
  }
  
  get national() {
    return this.control('national');
  }
  
  get postcode() {
    return this.control('postcode');
  }
  
  get provisionalRanger() {
    return this.control('provisionalRanger');
  }
  
  get status() {
    return this.control('status');
  }
  
  get town() {
    return this.control('town');
  }
  
  get updatedBy() {
    return this.control('updatedBy');
  }
  
  get updatedDate() {
    return this.control('updatedDate');
  }
  
  get volunteerRanger() {
    return this.control('volunteerRanger');
  }
  
  get wildlifeManagerRanger() {
    return this.control('wildlifeManagerRanger');
  }
  
}





